/**
 * /admin/insurance/policies
 * -------------------------
 * Single-page operations console for active + arrears insurance policies.
 * Pulls from /api/admin/master-policy-applications and filters client-side by
 * status, sorting overdue policies to the top.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ShieldCheck, Clock, Search, ExternalLink } from "lucide-react";
import api from "../../lib/api";
import { Input } from "../../components/ui/input";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const TONE = {
  active:           "bg-emerald-50 border-emerald-200 text-emerald-800",
  awaiting_payment: "bg-amber-50 border-amber-200 text-amber-800",
  cancelled:        "bg-rose-50 border-rose-200 text-rose-800",
  quoted:           "bg-sky-50 border-sky-200 text-sky-800",
};

export default function AdminInsurancePolicies() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("arrears");

  useEffect(() => {
    api.get("/admin/master-policy-applications")
      .then((r) => setApps(r.data?.applications || r.data || []))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const rows = apps.filter((a) => {
      if (tab === "arrears") return a.status === "awaiting_payment" && (a.arrears_days || 0) > 0;
      if (tab === "active")  return a.status === "active";
      if (tab === "current") return a.status === "awaiting_payment" && !(a.arrears_days || 0);
      if (tab === "cancelled") return a.status === "cancelled";
      return true;
    });
    const needle = q.trim().toLowerCase();
    if (!needle) return rows.sort((a, b) => (b.arrears_days || 0) - (a.arrears_days || 0));
    return rows.filter((a) =>
      (a.seller_name || "").toLowerCase().includes(needle) ||
      (a.seller_email || "").toLowerCase().includes(needle) ||
      (a.allianz_policy_id || "").toLowerCase().includes(needle),
    );
  }, [apps, q, tab]);

  const counts = useMemo(() => ({
    arrears:   apps.filter((a) => a.status === "awaiting_payment" && (a.arrears_days || 0) > 0).length,
    current:   apps.filter((a) => a.status === "awaiting_payment" && !(a.arrears_days || 0)).length,
    active:    apps.filter((a) => a.status === "active").length,
    cancelled: apps.filter((a) => a.status === "cancelled").length,
  }), [apps]);

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="admin-insurance-policies">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="display text-3xl tracking-tight">Insurance · Policies</h1>
          <p className="text-sm text-foreground/55 mt-1">Active master policies + arrears watchlist. Auto-debit runs daily.</p>
        </div>
        <Link to="/admin/master-policy" className="text-sm underline text-foreground/65 hover:text-foreground" data-testid="admin-ins-back-queue">
          ← Back to application queue
        </Link>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-5">
        {[
          { v: "arrears",   l: "Arrears",     n: counts.arrears,   icon: AlertTriangle },
          { v: "current",   l: "Current installments", n: counts.current, icon: Clock },
          { v: "active",    l: "Active",      n: counts.active,    icon: ShieldCheck },
          { v: "cancelled", l: "Cancelled",   n: counts.cancelled, icon: AlertTriangle },
        ].map(t => {
          const Icon = t.icon;
          const sel = tab === t.v;
          return (
            <button
              key={t.v}
              type="button"
              onClick={() => setTab(t.v)}
              data-testid={`admin-ins-tab-${t.v}`}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-full border text-[13px] font-medium transition-colors ${sel ? "border-foreground bg-foreground text-background" : "border-border hover:border-foreground/30"}`}
            >
              <Icon className="w-4 h-4" />
              {t.l} <span className={`mono text-[11px] ${sel ? "text-background/70" : "text-foreground/55"}`}>{t.n}</span>
            </button>
          );
        })}
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search seller name / email / policy #"
          className="pl-9 h-10"
          data-testid="admin-ins-search"
        />
      </div>

      <div className="rounded-2xl border border-border bg-white overflow-hidden">
        <div className="grid grid-cols-[1.6fr_1fr_1fr_1fr_1fr_0.7fr_0.4fr] border-b border-border bg-secondary/40 text-[11px] uppercase tracking-wider text-foreground/55 font-medium">
          {["Seller", "Plan", "Premium", "Outstanding", "Next due", "Arrears", ""].map((h, i) => (
            <div key={i} className="px-4 py-3">{h}</div>
          ))}
        </div>
        {loading ? (
          <div className="p-8 text-center text-foreground/45 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-foreground/45 text-sm" data-testid="admin-ins-empty">No policies in this view.</div>
        ) : filtered.map((a) => (
          <div key={a.id} className="grid grid-cols-[1.6fr_1fr_1fr_1fr_1fr_0.7fr_0.4fr] border-b border-border last:border-b-0 hover:bg-secondary/20" data-testid={`admin-ins-row-${a.id}`}>
            <div className="px-4 py-3">
              <div className="text-sm font-medium">{a.seller_name || "—"}</div>
              <div className="text-[11px] text-foreground/55">{a.seller_email}</div>
              <span className={`inline-block mt-1 text-[10px] mono uppercase tracking-wider px-2 py-0.5 rounded-full border ${TONE[a.status] || "bg-secondary border-border"}`}>{a.status}</span>
            </div>
            <div className="px-4 py-3 text-sm">{a.payment_plan || "—"}</div>
            <div className="px-4 py-3 text-sm mono">{money(a.quote_premium_total)}</div>
            <div className="px-4 py-3 text-sm mono">{money(a.amount_outstanding)}</div>
            <div className="px-4 py-3 text-sm mono">{a.next_installment_due || "—"}</div>
            <div className={`px-4 py-3 text-sm mono ${(a.arrears_days || 0) > 0 ? "text-amber-700 font-semibold" : "text-foreground/45"}`}>
              {(a.arrears_days || 0) > 0 ? `${a.arrears_days}d` : "—"}
            </div>
            <div className="px-4 py-3 flex items-center">
              <Link to={`/admin/master-policy?focus=${a.id}`} className="text-foreground/55 hover:text-foreground" data-testid={`admin-ins-open-${a.id}`}>
                <ExternalLink className="w-4 h-4" />
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
